/**
 * Admin Login Page — Platform Operations
 *
 * A separate login experience for the platform admin team.
 * Minimal, dark-themed UI without the marketing Navbar/Footer.
 * After successful login, validates admin group membership and
 * redirects to /admin.
 */

import React, { useState, FormEvent } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import * as authService from '../../services/auth.service';
import { Shield, AlertCircle, ShieldCheck } from 'lucide-react';

export default function AdminLoginPage() {
  const { login, completeNewPassword, logout, error, clearError, isLoading, needsNewPassword } =
    useAuth();
  const navigate = useNavigate();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [localError, setLocalError] = useState('');

  const handleLogin = async (e: FormEvent) => {
    e.preventDefault();
    clearError();
    setLocalError('');

    const ok = await login(email, password);
    if (!ok) return;

    // needsNewPassword will be set on first login — handled below
    if (!needsNewPassword) {
      // Verify the logged-in user is actually an admin.
      // Read directly from Cognito session (not React state) for immediate accuracy.
      if (!authService.isAdmin()) {
        setLocalError('Access denied. This login is for platform administrators only.');
        logout();
        return;
      }
      navigate('/admin');
    }
  };

  const handleNewPassword = async (e: FormEvent) => {
    e.preventDefault();
    clearError();
    setLocalError('');

    if (newPassword !== confirmPassword) {
      setLocalError('Passwords do not match.');
      return;
    }
    if (newPassword.length < 12) {
      setLocalError('Password must be at least 12 characters.');
      return;
    }

    const ok = await completeNewPassword(newPassword);
    if (ok) {
      if (!authService.isAdmin()) {
        setLocalError('Access denied. This login is for platform administrators only.');
        logout();
        return;
      }
      navigate('/admin');
    }
  };

  const displayError = error || localError;

  // ── New Password flow (first login with temp password) ──
  if (needsNewPassword) {
    return (
      <div style={styles.page}>
        <div style={styles.container}>
          <div style={styles.card}>
            <div style={styles.header}>
              <ShieldCheck size={32} color="#6366f1" />
              <h1 style={styles.title}>Set New Password</h1>
              <p style={styles.subtitle}>
                Your account was created with a temporary password. Please set a
                permanent one.
              </p>
            </div>

            {displayError && (
              <div style={styles.error}>
                <AlertCircle size={16} /> {displayError}
              </div>
            )}

            <form onSubmit={handleNewPassword}>
              <div style={styles.formGroup}>
                <label style={styles.label}>New Password</label>
                <input
                  type="password"
                  placeholder="Min 12 chars, upper, lower, number, symbol"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  style={styles.input}
                  required
                  minLength={12}
                />
              </div>
              <div style={styles.formGroup}>
                <label style={styles.label}>Confirm Password</label>
                <input
                  type="password"
                  placeholder="Re-enter your new password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  style={styles.input}
                  required
                />
              </div>
              <button type="submit" style={styles.button} disabled={isLoading}>
                {isLoading ? 'Setting password...' : 'Set Password & Enter'}
              </button>
            </form>
          </div>
        </div>
      </div>
    );
  }

  // ── Regular admin login ──
  return (
    <div style={styles.page}>
      <div style={styles.container}>
        <div style={styles.card}>
          <div style={styles.header}>
            <Shield size={32} color="#6366f1" />
            <h1 style={styles.title}>Platform Administration</h1>
            <p style={styles.subtitle}>
              token-kit operations console. Authorized personnel only.
            </p>
          </div>

          {displayError && (
            <div style={styles.error}>
              <AlertCircle size={16} /> {displayError}
            </div>
          )}

          <form onSubmit={handleLogin}>
            <div style={styles.formGroup}>
              <label style={styles.label}>Email</label>
              <input
                type="email"
                placeholder="admin@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                style={styles.input}
                required
              />
            </div>
            <div style={styles.formGroup}>
              <label style={styles.label}>Password</label>
              <input
                type="password"
                placeholder="Enter your password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                style={styles.input}
                required
              />
            </div>
            <button type="submit" style={styles.button} disabled={isLoading}>
              {isLoading ? 'Authenticating...' : 'Sign In'}
            </button>
          </form>

          <p style={styles.footer}>
            <Link to="/" style={styles.link}>
              &larr; Back to token-kit.com
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}

// ── Inline styles for a self-contained dark-themed admin login ──
const styles: Record<string, React.CSSProperties> = {
  page: {
    minHeight: '100vh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: 'linear-gradient(135deg, #0f172a 0%, #1e1b4b 50%, #0f172a 100%)',
    fontFamily:
      "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
  },
  container: {
    width: '100%',
    maxWidth: 420,
    padding: '0 16px',
  },
  card: {
    background: 'rgba(30, 27, 75, 0.6)',
    backdropFilter: 'blur(20px)',
    border: '1px solid rgba(99, 102, 241, 0.2)',
    borderRadius: 16,
    padding: '40px 32px',
  },
  header: {
    textAlign: 'center' as const,
    marginBottom: 28,
  },
  title: {
    color: '#e2e8f0',
    fontSize: 22,
    fontWeight: 700,
    margin: '12px 0 4px',
  },
  subtitle: {
    color: '#94a3b8',
    fontSize: 14,
    margin: 0,
    lineHeight: 1.5,
  },
  error: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    background: 'rgba(239, 68, 68, 0.15)',
    color: '#fca5a5',
    border: '1px solid rgba(239, 68, 68, 0.3)',
    borderRadius: 8,
    padding: '10px 14px',
    fontSize: 13,
    marginBottom: 20,
  },
  formGroup: {
    marginBottom: 18,
  },
  label: {
    display: 'block',
    color: '#cbd5e1',
    fontSize: 13,
    fontWeight: 500,
    marginBottom: 6,
  },
  input: {
    width: '100%',
    padding: '10px 14px',
    fontSize: 14,
    color: '#e2e8f0',
    background: 'rgba(15, 23, 42, 0.6)',
    border: '1px solid rgba(99, 102, 241, 0.3)',
    borderRadius: 8,
    outline: 'none',
    boxSizing: 'border-box' as const,
  },
  button: {
    width: '100%',
    padding: '12px 0',
    fontSize: 14,
    fontWeight: 600,
    color: '#fff',
    background: 'linear-gradient(135deg, #6366f1, #8b5cf6)',
    border: 'none',
    borderRadius: 8,
    cursor: 'pointer',
    marginTop: 8,
  },
  footer: {
    textAlign: 'center' as const,
    margin: '20px 0 0',
    fontSize: 13,
  },
  link: {
    color: '#818cf8',
    textDecoration: 'none',
  },
};
